/**
 * Meta Pixel helper for Santa Mónica Residencial.
 *
 * - trackEvent(name, params): dispatches fbq('track', ...) with a unique eventID
 *   and returns the eventID so it can be forwarded to the backend for CAPI dedup.
 * - trackCustom(name, params): same as above but uses fbq('trackCustom', ...).
 * - getBrowserSignals(): returns { event_id, event_source_url, fbp, fbc } captured
 *   right before a form submission. Pass these to the backend together with the
 *   lead data to deduplicate with the server-side Conversions API later.
 *
 * Resilient to the Pixel script failing to load: every helper is a no-op when
 * window.fbq is unavailable, so no user flow is ever broken.
 */

const COOKIE_FBP = '_fbp';
const COOKIE_FBC = '_fbc';

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const prefix = `${name}=`;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function newEventId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* ignore */ }
  // Fallback for very old browsers
  return 'ev-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Fire a standard Meta Pixel event with deduplication (eventID).
 * Returns the generated event_id so the caller can forward it to the backend.
 */
export function trackEvent(eventName, params = {}) {
  const eventId = newEventId();
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', eventName, params, { eventID: eventId });
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[metaTrack]', eventName, e);
  }
  return eventId;
}

/**
 * Fire a custom (non-standard) event.
 */
export function trackCustom(eventName, params = {}) {
  const eventId = newEventId();
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('trackCustom', eventName, params, { eventID: eventId });
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[metaTrack custom]', eventName, e);
  }
  return eventId;
}

/**
 * Fire PageView for the current URL. Called from the router on every route change.
 */
export function trackPageView() {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[metaTrack PageView]', e);
  }
}

/**
 * Build the browser-signal bundle to send to the backend together with lead data.
 * Should be called right before posting a lead so eventID matches the pixel event.
 *
 * @param {string} [eventId]  Optional eventID already produced by trackEvent().
 *                            If omitted, a new one is generated here (useful for
 *                            server-only events).
 * @returns {{event_id:string, event_source_url:string, fbp:(string|null), fbc:(string|null)}}
 */
export function getBrowserSignals(eventId) {
  const id = eventId || newEventId();
  const url = typeof window !== 'undefined' ? window.location.href : '';
  return {
    event_id: id,
    event_source_url: url,
    fbp: readCookie(COOKIE_FBP),
    fbc: readCookie(COOKIE_FBC),
  };
}
